import {once} from '@packages/utilities/memo';
import {requestIdleCallback} from '@packages/domains/browser-polyfills/idle-callback';

type Thunk<a> = (arg1: undefined) => a;

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
/** A value that can be computed only when needed. */
export type Lazy<a> = Thunk<a>;

/** Produce a lazy value which allows for it to be computed only when needed. */
export const defer: <a>(arg1: Thunk<a>) => Lazy<a> = once as any;

/**
 * Get the computed value from a `Lazy` value.
 *
 * The value will only be computed once, and cached thereafter.
 */
// @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
export const force = <a>(f: Lazy<a>): a => f();

/**
 * Produce a lazy value and schedule force() to be called when the browser is idle.
 *
 * This meant to be used where not forcing the lazy value holds onto memory that would otherwise be freed.
 * In this case, it is better to force the computation when the browser is idle so that the memory can be freed.
 */
export const deferUntilIdle = <a>(fn: Thunk<a>): Lazy<a> => {
  const lazyValue = defer(fn);

  // We use a weak ref to prevent the idle callback from being the only memory handle to the lazy value.
  // That allows the closure to be garbage collected and the potentially expensive lazy computation will be skipped.
  const lazyValueRef = new WeakRef(lazyValue);
  requestIdleCallback(() => {
    const maybeLazyValueRef = lazyValueRef.deref();
    if (maybeLazyValueRef) {
      force(maybeLazyValueRef);
    }
  });

  return lazyValue;
};
